import { Link } from "react-router-dom";
import { ShieldCheck, ClipboardCheck, FileText, CalendarClock, ArrowRight, CheckCircle2 } from "lucide-react";
import { FadeIn } from "@/components/Reveal";

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: "Risk Management", desc: "Identify, evaluate, and track potential business risks." },
  { icon: ClipboardCheck, title: "Audit Readiness", desc: "Organize reviews, findings, and corrective actions." },
  { icon: FileText, title: "Operational Documentation", desc: "Establish SOPs and assign business responsibilities." },
  { icon: CalendarClock, title: "Compliance Tracking", desc: "Monitor licenses, insurance, documents, and important deadlines." },
];

export const MitivisePromo = () => (
  <section className="noise-dark relative bg-navydeep py-24 text-white lg:py-28" data-testid="home-cos-section">
    <div className="container-tsm relative grid max-w-[1200px] items-center gap-14 lg:grid-cols-2">
      <div>
        <FadeIn>
          <p className="eyebrow">The TSM4 Digital Platform</p>
          <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl" data-testid="home-cos-headline">
            Introducing MITIVISE
          </h2>
          <p className="mt-4 text-xl font-semibold text-gold" data-testid="home-cos-subheadline">
            Your Business Deserves More Than a To-Do List. It Needs a System.
          </p>
          <p className="mt-6 max-w-xl leading-relaxed text-white/70" data-testid="home-cos-copy">
            Running a business involves more than delivering a service. It means managing risks, maintaining
            documentation, tracking responsibilities, and preparing for the unexpected. MITIVISE by TSM Group CLE
            gives small business owners the tools to organize these essential processes in one practical, easy-to-use
            digital solution.
          </p>
          <p className="mt-4 max-w-xl leading-relaxed text-white/70">
            From tracking business risks and audits to documenting operating procedures and monitoring important
            deadlines, MITIVISE helps you build the operational foundation your business needs.
          </p>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {HIGHLIGHTS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`home-cos-feature-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="h-5 w-5 text-gold" />
                <p className="mt-2 text-sm font-bold">{title}</p>
                <p className="mt-1 text-xs leading-relaxed text-white/60">{desc}</p>
              </div>
            ))}
          </div>
        </FadeIn>
        <FadeIn delay={0.15}>
          <p className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2 text-sm font-semibold text-gold" data-testid="home-cos-price">
            <CheckCircle2 className="h-4 w-4" />
            MITIVISE Toolkit: $79 one-time — no subscription required.
          </p>
          <div className="mt-6 flex flex-col gap-4 sm:flex-row">
            <Link to="/mitivise" data-testid="home-cos-explore" className="btn-gold">
              Discover MITIVISE
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="/mitivise#buy" data-testid="home-cos-buy" className="btn-outline-light">
              Get the Toolkit — $79
            </a>
          </div>
        </FadeIn>
      </div>
      <FadeIn delay={0.2} className="lg:justify-self-end">
        <figure className="rounded-lg border border-teal/30 bg-navy p-3 shadow-2xl">
          <img
            src="/previews/dashboard.png"
            alt="The MITIVISE Excel dashboard showing live compliance metrics"
            className="w-full rounded-sm"
            loading="lazy"
          />
          <figcaption className="px-2 py-3 text-center text-xs text-white/50">
            The MITIVISE Dashboard — rendered from the actual workbook.
          </figcaption>
        </figure>
      </FadeIn>
    </div>
  </section>
);
